import type { Board } from "../board";
import { ALL_DIRECTIONS, type Direction, reverseDirection } from "../direction";
import type { ControllableByAI } from "../interfaces";
import { MIN_FIRE_DISTANCE } from "../mobs/blackHoleRobot";
import { Passability } from "../passability";
import { BlackHoleProjectile } from "../projectiles/blackHoleProjectile";
import { deserializeXY, serializeXY } from "../serialization";
import { DebugIndicator } from "../things/debugIndicator";
import { XY } from "../xy";
import { BasicAI } from "./basicAI";
import { bumpDoorIfClosed, canMobStandIn, updateLookDirectionFromHorizontalDelta } from "./movement";
import { findPath, type StepCost } from "./pathfinder";

const EDGE_WEIGHT = 0.5;
const TRAVEL_WEIGHT = 0.25;
const STICKINESS_BONUS = 4;
const BORDER_PENALTY = 100;
const PLAYER_AVOID_RADIUS = 2;
const PLAYER_PROXIMITY_COST = 30;
// Strictly inside the avoid bubble, so transiting its costly outer ring never triggers flee ping-pong.
const PERSONAL_SPACE_RADIUS = PLAYER_AVOID_RADIUS - 1;
const DEBUG_SHOW_DESTINATION = false;

export class BlackHoleRobotAI extends BasicAI {
	private currentDestination: XY | null = null;
	private escaping = false;

	constructor(actor: ControllableByAI) {
		super(actor);
		this.alwaysKnowsWhereTargetIs = true;
	}

	override processTurn(): void {
		if (!this.actor.isMovementReady()) return;
		const player = this.getBoard().player;
		if (!player || !player.active) return;

		const previousXY = this.actor.getXY();
		if (!this.actor.getXY().isInSquareRadius(player.xy, PLAYER_AVOID_RADIUS)) this.escaping = false;
		const inPersonalSpace = this.actor.getXY().isInSquareRadius(player.xy, PERSONAL_SPACE_RADIUS);
		const fled = !this.escaping && inPersonalSpace && this.flee(player.xy);
		if (!fled) {
			// A cornered boss whose greedy flee has no move commits to the A* route until clear,
			// otherwise flee and the route undo each other around the player's ring.
			if (inPersonalSpace) this.escaping = true;
			this.moveTowardsFiringPosition(player.xy);
		}
		updateLookDirectionFromHorizontalDelta(this.actor, previousXY, this.actor.getXY());
	}

	private moveTowardsFiringPosition(playerXY: XY): void {
		this.currentDestination = this.pickDestination(playerXY);
		if (this.currentDestination !== null) {
			this.stepTowards(this.currentDestination);
		} else {
			this.flee(playerXY);
		}
		if (DEBUG_SHOW_DESTINATION && this.currentDestination !== null) {
			this.getBoard().addThing(new DebugIndicator(this.currentDestination));
		}
	}

	private pickDestination(playerXY: XY): XY | null {
		const board = this.getBoard();
		const travelMap = board.getCachedDistanceMap(this.actor.getXY(), Passability.THROUGH_DOORS);
		const boardCenter = new XY(Math.floor(board.width / 2), Math.floor(board.height / 2));

		let best: XY | null = null;
		let bestScore = -Infinity;
		for (const candidate of this.rayCandidates(board, playerXY, this.shadowedDirections(board, playerXY))) {
			const travelCost = travelMap.get(candidate);
			if (travelCost === undefined) continue;
			const score =
				candidate.distanceTo(playerXY) +
				EDGE_WEIGHT * candidate.distanceTo(boardCenter) -
				TRAVEL_WEIGHT * travelCost -
				(this.isBorderCell(board, candidate) ? BORDER_PENALTY : 0) +
				(this.currentDestination !== null && candidate.equals(this.currentDestination) ? STICKINESS_BONUS : 0);
			if (score > bestScore) {
				bestScore = score;
				best = candidate;
			}
		}
		return best;
	}

	private isBorderCell(board: Board, xy: XY): boolean {
		return xy.x === 0 || xy.y === 0 || xy.x === board.width - 1 || xy.y === board.height - 1;
	}

	private shadowedDirections(board: Board, playerXY: XY): Set<Direction> {
		const shadowed = new Set<Direction>();
		for (const thing of board.getAllThings()) {
			if (!(thing instanceof BlackHoleProjectile) || !thing.active) continue;
			if (!thing.shadows(playerXY)) continue;
			shadowed.add(thing.direction);
			shadowed.add(reverseDirection(thing.direction));
		}
		return shadowed;
	}

	private rayCandidates(board: Board, playerXY: XY, shadowedDirs: Set<Direction>): XY[] {
		const candidates: XY[] = [];
		for (const dir of ALL_DIRECTIONS) {
			if (shadowedDirs.has(dir)) continue;
			for (let xy = playerXY.addDirection(dir); board.isInBounds(xy); xy = xy.addDirection(dir)) {
				if (xy.distanceTo(playerXY) < MIN_FIRE_DISTANCE) continue;
				if (canMobStandIn(board, xy)) candidates.push(xy);
			}
		}
		return candidates;
	}

	private stepTowards(destination: XY): void {
		const path = findPath(this.getBoard(), this.actor.getXY(), destination, this.stepCost);
		if (!path || path.length === 0) {
			this.currentDestination = null;
			return;
		}
		this.moveActorTo(path[0]);
	}

	private stepCost: StepCost = (_from, to) => {
		const board = this.getBoard();
		if (!canMobStandIn(board, to)) return null;
		if (board.player && to.isInSquareRadius(board.player.xy, PLAYER_AVOID_RADIUS)) return PLAYER_PROXIMITY_COST;
		return 1;
	};

	protected override moveActorTo(xy: XY): void {
		bumpDoorIfClosed(this.getBoard(), xy, this.actor as any);
		super.moveActorTo(xy);
	}

	private flee(playerXY: XY): boolean {
		const board = this.getBoard();
		let best: XY | null = null;
		let bestDistance = this.actor.getXY().distanceTo(playerXY);
		for (const neighbor of this.actor.getXY().getNeighbors()) {
			if (!canMobStandIn(board, neighbor)) continue;
			const distance = neighbor.distanceTo(playerXY);
			if (distance > bestDistance) {
				bestDistance = distance;
				best = neighbor;
			}
		}
		if (best === null) return false;
		this.moveActorTo(best);
		return true;
	}

	override serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			currentDestination: this.currentDestination ? serializeXY(this.currentDestination) : null,
			escaping: this.escaping,
		};
	}

	override restoreFrom(data: any): void {
		if (!data) return;
		super.restoreFrom(data);
		this.currentDestination = data.currentDestination ? deserializeXY(data.currentDestination) : null;
		this.escaping = data.escaping ?? false;
	}
}
